import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  dbRowToStockItem,
  STOCK_ITEM_COLUMNS,
  STOCK_PACKAGE_COLUMNS,
  dbRowToPackage,
} from '@/logic/stockMapper';
import {
  dbRowToRecipe,
  dbRowToRecipeIngredient,
  RECIPE_COLUMNS,
  RECIPE_INGREDIENT_COLUMNS,
  dbRowToMealPlan,
  dbRowToMealPlanIngredient,
  dbRowToMealPlanAttendee,
  MEAL_PLAN_COLUMNS,
  MEAL_PLAN_INGREDIENT_COLUMNS,
  MEAL_PLAN_ATTENDEE_COLUMNS,
} from '@/logic/mealMapper';
import {
  saveStockItemsToCache,
  saveStockPackagesToCache,
} from '@/lib/stockCache';
import { saveRecipesToCache, saveMealPlansToCache } from '@/lib/mealCache';
import { formatLocalDate } from '@/lib/dateUtils';
import type { MealPlanIngredient, MealPlanAttendee, RecipeIngredient } from '@/types/meal';

/**
 * Background prefetchers — populate the React Query cache (and localStorage)
 * for module data so when the user clicks a tab, data is already there.
 *
 * These functions mirror the queryFn of their respective hooks (useStock,
 * useStockPackages, useRecipes, useMealPlans). They DO NOT subscribe to
 * realtime — that responsibility stays with the hook when the view mounts.
 * Same queryKey is used so the warmed entry is consumed transparently.
 */

const STOCK_STALE = 5 * 60 * 1000;
const RECIPES_STALE = 60 * 1000;
const MEALS_STALE = 60 * 1000;

export async function prefetchStockItems(qc: QueryClient, teamId: string) {
  await qc.prefetchQuery({
    queryKey: ['stock-items', teamId],
    staleTime: STOCK_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stock_items')
        .select(STOCK_ITEM_COLUMNS)
        .eq('team_id', teamId)
        .order('category', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      const items = (data || []).map(dbRowToStockItem);
      saveStockItemsToCache(teamId, items);
      return items;
    },
  });
}

export async function prefetchStockPackages(qc: QueryClient, teamId: string) {
  await qc.prefetchQuery({
    queryKey: ['stock-packages', teamId],
    staleTime: STOCK_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stock_packages')
        .select(STOCK_PACKAGE_COLUMNS)
        .eq('team_id', teamId)
        .order('status', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      const pkgs = (data || []).map(dbRowToPackage);
      saveStockPackagesToCache(teamId, pkgs);
      return pkgs;
    },
  });
}

export async function prefetchRecipes(qc: QueryClient, teamId: string) {
  await qc.prefetchQuery({
    queryKey: ['recipes', teamId],
    staleTime: RECIPES_STALE,
    queryFn: async () => {
      const { data: recipeRows, error } = await supabase
        .from('recipes')
        .select(RECIPE_COLUMNS)
        .eq('team_id', teamId)
        .is('deleted_at', null)
        .order('name', { ascending: true });
      if (error) throw error;
      const ids = (recipeRows || []).map((r: any) => r.id);
      let ingredientsByRecipe: Record<string, RecipeIngredient[]> = {};
      if (ids.length) {
        const { data: ingRows, error: ingErr } = await supabase
          .from('recipe_ingredients')
          .select(RECIPE_INGREDIENT_COLUMNS)
          .in('recipe_id', ids)
          .order('sort_order', { ascending: true });
        if (ingErr) throw ingErr;
        ingredientsByRecipe = (ingRows || []).reduce(
          (acc: Record<string, RecipeIngredient[]>, row: any) => {
            const ing = dbRowToRecipeIngredient(row);
            (acc[ing.recipeId] ||= []).push(ing);
            return acc;
          },
          {}
        );
      }
      const items = (recipeRows || []).map((row: any) => ({
        ...dbRowToRecipe(row),
        ingredients: ingredientsByRecipe[row.id] || [],
      }));
      saveRecipesToCache(teamId, items);
      return items;
    },
  });
}

export async function prefetchMealPlans(qc: QueryClient, teamId: string, horizonDays = 14) {
  await qc.prefetchQuery({
    queryKey: ['meal-plans', teamId, horizonDays],
    staleTime: MEALS_STALE,
    queryFn: async () => {
      const from = new Date();
      from.setHours(0, 0, 0, 0);
      from.setDate(from.getDate() - 2);
      const to = new Date();
      to.setHours(23, 59, 59, 999);
      to.setDate(to.getDate() + horizonDays);

      const { data: planRows, error } = await supabase
        .from('meal_plans')
        .select(MEAL_PLAN_COLUMNS)
        .eq('team_id', teamId)
        .is('deleted_at', null)
        .gte('planned_date', formatLocalDate(from))
        .lte('planned_date', formatLocalDate(to))
        .order('planned_date', { ascending: true });
      if (error) throw error;

      const ids = (planRows || []).map((r: any) => r.id);
      let ingredientsByPlan: Record<string, MealPlanIngredient[]> = {};
      let attendeesByPlan: Record<string, MealPlanAttendee[]> = {};
      if (ids.length) {
        const [{ data: ingRows, error: ingErr }, { data: attRows, error: attErr }] =
          await Promise.all([
            supabase
              .from('meal_plan_ingredients')
              .select(MEAL_PLAN_INGREDIENT_COLUMNS)
              .in('meal_plan_id', ids)
              .order('sort_order', { ascending: true }),
            supabase
              .from('meal_plan_attendees')
              .select(MEAL_PLAN_ATTENDEE_COLUMNS)
              .in('meal_plan_id', ids),
          ]);
        if (ingErr) throw ingErr;
        if (attErr) throw attErr;
        ingredientsByPlan = (ingRows || []).reduce(
          (acc: Record<string, MealPlanIngredient[]>, row: any) => {
            const ing = dbRowToMealPlanIngredient(row);
            (acc[ing.mealPlanId] ||= []).push(ing);
            return acc;
          },
          {}
        );
        attendeesByPlan = (attRows || []).reduce(
          (acc: Record<string, MealPlanAttendee[]>, row: any) => {
            const att = dbRowToMealPlanAttendee(row);
            (acc[att.mealPlanId] ||= []).push(att);
            return acc;
          },
          {}
        );
      }

      const plans = (planRows || []).map((row: any) => ({
        ...dbRowToMealPlan(row),
        ingredients: ingredientsByPlan[row.id] || [],
        attendees: attendeesByPlan[row.id] || [],
      }));
      saveMealPlansToCache(teamId, plans);
      return plans;
    },
  });
}

export async function prefetchAllModules(qc: QueryClient, teamId: string) {
  // Fire in parallel; swallow individual failures so one bad module doesn't
  // block the others. Background work — never throws.
  await Promise.allSettled([
    prefetchStockItems(qc, teamId),
    prefetchStockPackages(qc, teamId),
    prefetchRecipes(qc, teamId),
    prefetchMealPlans(qc, teamId),
  ]);
}